import { Component, HostListener, inject, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute, Router } from '@angular/router';
import { RequestStatusV2 } from '@banyan/commons/enum/request-status-v2';
import { ProductV2 } from '@banyan/commons/models/order/product.model';
import { RequestFragranceProfile } from '@banyan/commons/models/request-details.model';
import {
  AdminRequestSubmitModalComponent
} from '@banyan/modules/admin/components/request/admin-request-submit-modal/admin-request-submit-modal.component';
import { AdminService } from '@banyan/modules/admin/services/admin.service';
import { Observable, take } from 'rxjs';

import { AdminRequestDetailModalComponent } from '../admin-request-detail-modal/admin-request-detail-modal.component';

@Component({
  selector: 'app-admin-request-details',
  templateUrl: './admin-request-details.component.html',
  styleUrl: './admin-request-details.component.scss',
})
export class AdminRequestDetailsComponent {
  @ViewChild('triggerMenu') menuTrigger: MatMenuTrigger | undefined;
  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    return (this.adminService.canSubmit() || this.checkDisabled()) && !this.adminService.isProductChange();
  }
  adminService = inject(AdminService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  dialog = inject(MatDialog);
  requestId: number | null = null;
  endUseId: number | null = null;
  activeTab: 'proposals' | 'details' = 'proposals';

  constructor() {
    this.requestId = Number.parseInt(this.route.snapshot.params['id'], 10);
    this.endUseId = Number.parseInt(this.route.snapshot.params['endUseId'], 10);
    this.adminService.getRequest(this.requestId, this.endUseId);
  }

  backToRequests(): void {
    this.router.navigate(['/admin/requests']).then();
  }

  transformRequestNotes(fragrance: RequestFragranceProfile | undefined): string[] {
    const notes: string[] = [];
    if (fragrance?.noteSuggestions) {
      fragrance.noteSuggestions.forEach(suggestion => {
        notes.push(suggestion.note);
      });
    }
    return notes;
  }

  openPopup(): void {
    this.menuTrigger?.openMenu();
  }

  closePopup(): void {
    this.menuTrigger?.closeMenu();
  }

  openProductsSearchModal(): void {
    const dialogRef = this.dialog.open(AdminRequestDetailModalComponent,
      {
        maxWidth: '1140px',
        maxHeight: '600px',
        width: '85vw',
        height: '80vh'
      });
    dialogRef
      .backdropClick()
      .pipe(take(1))
      .subscribe(() => {
        dialogRef.close();
      });
    }

  onRemoveProduct(product: ProductV2): void {
    this.adminService.checkRemovedProduct(product);
  }

  getVolumeInfo(code: string): string | null {
    return this.adminService.volumeChoices().find(elem => elem.code === code)?.name ?? null;
  }

  saveOrSubmitProducts(): void {
    if (this.adminService.canSubmit()) {
      const dialogRef = this.dialog.open(AdminRequestSubmitModalComponent,
        {
          maxWidth: '744px',
          maxHeight: '550px',
          data: {requestId: this.requestId, endUseId: this.endUseId},
        });
      dialogRef
        .backdropClick()
        .pipe(take(1))
        .subscribe(() => {
          dialogRef.close();
        });
    } else {
      this.adminService.saveRequestProduct(this.adminService.request()?.id ?? 0, this.endUseId ?? 0,
        this.adminService.request()?.brief.id ?? 0, this.adminService.request()?.fragranceProfiles[0].id ?? 0);
    }
  }

  checkDisabled(): boolean {
    const isEditing = this.adminService.isEditing();
    const hasNoChanges = [
      this.adminService.newProducts(),
      this.adminService.updatedProducts(),
      this.adminService.selectedProducts(),
      this.adminService.deletedProducts()
    ].every(list => list.length === 0);

    const status = this.adminService.request()?.status;
    const isNotEditableStatus = status !== RequestStatusV2.new && status !== RequestStatusV2.proposal && status !== RequestStatusV2.updateRequested;

    return isEditing || hasNoChanges || isNotEditableStatus;
  }



  onProductUpdated(editedProduct: ProductV2) {
      this.adminService.checkUpdatedProduct(editedProduct);
  }

  checkDisplayAddProposals(): boolean {
    const selectedCount = this.adminService.selectedProducts().length;
    const requestStatus = this.adminService.request()?.status;

    const isUnderLimit = selectedCount < this.adminService.MAX_SELECTED_PRODUCTS;
    const isEditableStatus = requestStatus === RequestStatusV2.new || requestStatus === RequestStatusV2.proposal || requestStatus === RequestStatusV2.updateRequested;

    return isUnderLimit && isEditableStatus;
  }

  getStatusType(statusCode: string | undefined): 'brand' | 'notification' | 'info' | 'trend' | 'green' {
    switch (statusCode) {
      case RequestStatusV2.new:
        return 'notification';
      case RequestStatusV2.proposal:
        return 'green';
      case RequestStatusV2.expired:
        return 'notification';
      case RequestStatusV2.updateRequested:
        return 'info';
      default:
        return 'notification';
    }
  }

}
